import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, Trash2, Table } from 'lucide-react';

const SchedulesEditor = ({ project, onChange }) => {
  const doorSchedule = project.door_schedule || [];
  const windowSchedule = project.window_schedule || [];
  const finishSchedule = project.finish_schedule || [];
  
  // Door Schedule Functions
  const addDoorItem = () => {
    onChange({
      ...project,
      door_schedule: [
        ...doorSchedule,
        {
          number: `D${doorSchedule.length + 1}`,
          door_type: '',
          width: null,
          height: null,
          material: '',
          frame: '',
          hardware: '',
          finish: '',
          fire_rate: '',
          remarks: ''
        }
      ]
    });
  };
  
  const removeDoorItem = (index) => {
    onChange({
      ...project,
      door_schedule: doorSchedule.filter((_, i) => i !== index)
    });
  };
  
  const updateDoorItem = (index, field, value) => {
    onChange({
      ...project,
      door_schedule: doorSchedule.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      )
    });
  };
  
  // Window Schedule Functions
  const addWindowItem = () => {
    onChange({
      ...project,
      window_schedule: [
        ...windowSchedule,
        {
          number: `W${windowSchedule.length + 1}`,
          window_type: '',
          width: null,
          height: null,
          glazing: '',
          frame: '',
          material: '',
          finish: '',
          remarks: ''
        }
      ]
    });
  };
  
  const removeWindowItem = (index) => {
    onChange({
      ...project,
      window_schedule: windowSchedule.filter((_, i) => i !== index)
    });
  };
  
  const updateWindowItem = (index, field, value) => {
    onChange({
      ...project,
      window_schedule: windowSchedule.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      )
    });
  };
  
  // Finish Schedule Functions
  const addFinishItem = () => {
    onChange({
      ...project,
      finish_schedule: [
        ...finishSchedule,
        {
          room_number: `${finishSchedule.length + 1}`,
          room_name: '',
          floor_finish: '',
          base_finish: '',
          wall_finish: '',
          ceiling_finish: '',
          remarks: ''
        }
      ]
    });
  };
  
  const removeFinishItem = (index) => {
    onChange({
      ...project,
      finish_schedule: finishSchedule.filter((_, i) => i !== index)
    });
  };
  
  const updateFinishItem = (index, field, value) => {
    onChange({
      ...project,
      finish_schedule: finishSchedule.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      )
    });
  };
  
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
          Schedules
        </h2>
        <p className="text-slate-600 dark:text-slate-400">
          Manage door, window, and finish schedules for your project
        </p>
      </div>
      
      <Tabs defaultValue="doors" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="doors">
            Door Schedule ({doorSchedule.length})
          </TabsTrigger>
          <TabsTrigger value="windows">
            Window Schedule ({windowSchedule.length})
          </TabsTrigger>
          <TabsTrigger value="finishes">
            Finish Schedule ({finishSchedule.length})
          </TabsTrigger>
        </TabsList>
        
        {/* Door Schedule */}
        <TabsContent value="doors" className="space-y-4 mt-6">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
              Door Schedule
            </h3>
            <Button onClick={addDoorItem} className="bg-gradient-to-r from-blue-600 to-indigo-600">
              <Plus className="mr-2 h-4 w-4" />
              Add Door
            </Button>
          </div>
          
          {doorSchedule.length === 0 ? (
            <Card className="p-12 text-center">
              <Table className="mx-auto h-12 w-12 text-slate-400 mb-4" />
              <p className="text-slate-600 dark:text-slate-400 mb-4">
                No doors added yet
              </p>
              <Button onClick={addDoorItem} variant="outline">
                <Plus className="mr-2 h-4 w-4" />
                Add First Door
              </Button>
            </Card>
          ) : (
            <div className="space-y-4">
              {doorSchedule.map((door, index) => (
                <Card key={index} className="p-4">
                  <div className="flex items-start justify-between mb-4">
                    <span className="font-mono font-semibold text-blue-600">
                      {door.number}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeDoorItem(index)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <Input
                      placeholder="Type"
                      value={door.door_type}
                      onChange={(e) => updateDoorItem(index, 'door_type', e.target.value)}
                    />
                    <Input
                      placeholder="Width"
                      type="number"
                      value={door.width || ''}
                      onChange={(e) => updateDoorItem(index, 'width', parseFloat(e.target.value))}
                    />
                    <Input
                      placeholder="Height"
                      type="number"
                      value={door.height || ''}
                      onChange={(e) => updateDoorItem(index, 'height', parseFloat(e.target.value))}
                    />
                    <Input
                      placeholder="Material"
                      value={door.material}
                      onChange={(e) => updateDoorItem(index, 'material', e.target.value)}
                    />
                    <Input
                      placeholder="Frame"
                      value={door.frame}
                      onChange={(e) => updateDoorItem(index, 'frame', e.target.value)}
                    />
                    <Input
                      placeholder="Hardware"
                      value={door.hardware}
                      onChange={(e) => updateDoorItem(index, 'hardware', e.target.value)}
                    />
                    <Input
                      placeholder="Finish"
                      value={door.finish}
                      onChange={(e) => updateDoorItem(index, 'finish', e.target.value)}
                    />
                    <Input
                      placeholder="Fire Rate"
                      value={door.fire_rate}
                      onChange={(e) => updateDoorItem(index, 'fire_rate', e.target.value)}
                    />
                    <Input
                      placeholder="Remarks"
                      className="col-span-2 md:col-span-4"
                      value={door.remarks}
                      onChange={(e) => updateDoorItem(index, 'remarks', e.target.value)}
                    />
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
        
        {/* Window Schedule */}
        <TabsContent value="windows" className="space-y-4 mt-6">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
              Window Schedule
            </h3>
            <Button onClick={addWindowItem} className="bg-gradient-to-r from-blue-600 to-indigo-600">
              <Plus className="mr-2 h-4 w-4" />
              Add Window
            </Button>
          </div>
          
          {windowSchedule.length === 0 ? (
            <Card className="p-12 text-center">
              <Table className="mx-auto h-12 w-12 text-slate-400 mb-4" />
              <p className="text-slate-600 dark:text-slate-400 mb-4">
                No windows added yet
              </p>
              <Button onClick={addWindowItem} variant="outline">
                <Plus className="mr-2 h-4 w-4" />
                Add First Window
              </Button>
            </Card>
          ) : (
            <div className="space-y-4">
              {windowSchedule.map((window, index) => (
                <Card key={index} className="p-4">
                  <div className="flex items-start justify-between mb-4">
                    <span className="font-mono font-semibold text-blue-600">
                      {window.number}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeWindowItem(index)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <Input
                      placeholder="Type"
                      value={window.window_type}
                      onChange={(e) => updateWindowItem(index, 'window_type', e.target.value)}
                    />
                    <Input
                      placeholder="Width"
                      type="number"
                      value={window.width || ''}
                      onChange={(e) => updateWindowItem(index, 'width', parseFloat(e.target.value))}
                    />
                    <Input
                      placeholder="Height"
                      type="number"
                      value={window.height || ''}
                      onChange={(e) => updateWindowItem(index, 'height', parseFloat(e.target.value))}
                    />
                    <Input
                      placeholder="Glazing"
                      value={window.glazing}
                      onChange={(e) => updateWindowItem(index, 'glazing', e.target.value)}
                    />
                    <Input
                      placeholder="Frame"
                      value={window.frame}
                      onChange={(e) => updateWindowItem(index, 'frame', e.target.value)}
                    />
                    <Input
                      placeholder="Material"
                      value={window.material}
                      onChange={(e) => updateWindowItem(index, 'material', e.target.value)}
                    />
                    <Input
                      placeholder="Finish"
                      value={window.finish}
                      onChange={(e) => updateWindowItem(index, 'finish', e.target.value)}
                    />
                    <Input
                      placeholder="Remarks"
                      className="col-span-1"
                      value={window.remarks}
                      onChange={(e) => updateWindowItem(index, 'remarks', e.target.value)}
                    />
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
        
        {/* Finish Schedule */}
        <TabsContent value="finishes" className="space-y-4 mt-6">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
              Finish Schedule
            </h3>
            <Button onClick={addFinishItem} className="bg-gradient-to-r from-blue-600 to-indigo-600">
              <Plus className="mr-2 h-4 w-4" />
              Add Room
            </Button>
          </div>
          
          {finishSchedule.length === 0 ? (
            <Card className="p-12 text-center">
              <Table className="mx-auto h-12 w-12 text-slate-400 mb-4" />
              <p className="text-slate-600 dark:text-slate-400 mb-4">
                No finishes added yet
              </p>
              <Button onClick={addFinishItem} variant="outline">
                <Plus className="mr-2 h-4 w-4" />
                Add First Room
              </Button>
            </Card>
          ) : (
            <div className="space-y-4">
              {finishSchedule.map((finish, index) => (
                <Card key={index} className="p-4">
                  <div className="flex items-start justify-between mb-4">
                    <span className="font-mono font-semibold text-blue-600">
                      Room {finish.room_number}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeFinishItem(index)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <Input
                      placeholder="Room Name"
                      className="col-span-2 md:col-span-3"
                      value={finish.room_name}
                      onChange={(e) => updateFinishItem(index, 'room_name', e.target.value)}
                    />
                    <Input
                      placeholder="Floor Finish"
                      value={finish.floor_finish}
                      onChange={(e) => updateFinishItem(index, 'floor_finish', e.target.value)}
                    />
                    <Input
                      placeholder="Base Finish"
                      value={finish.base_finish}
                      onChange={(e) => updateFinishItem(index, 'base_finish', e.target.value)}
                    />
                    <Input
                      placeholder="Wall Finish"
                      value={finish.wall_finish}
                      onChange={(e) => updateFinishItem(index, 'wall_finish', e.target.value)}
                    />
                    <Input
                      placeholder="Ceiling Finish"
                      value={finish.ceiling_finish}
                      onChange={(e) => updateFinishItem(index, 'ceiling_finish', e.target.value)}
                    />
                    <Input
                      placeholder="Remarks"
                      className="col-span-2"
                      value={finish.remarks}
                      onChange={(e) => updateFinishItem(index, 'remarks', e.target.value)}
                    />
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SchedulesEditor;